import { Avatar, Banner, DataGrid, IconButton, StatusIndicator, Tooltip } from '@neo4j-ndl/react';
import { InformationCircleIconOutline, QuestionMarkCircleIconOutline } from '@neo4j-ndl/react/icons';
import { getFlag } from '@nx/launch-darkly-service';
import type { OrganizationMember } from '@nx/state';
import { ACTION, MFA_STATUS_REASON, consoleApi, usePermissions } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import { DataGridHelpers, SearchField } from '@nx/ui';
import { createColumnHelper, getFilteredRowModel } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useOrgAppContext } from '../../app-context';
import { useCommonTable } from '../../components/table';
import { ROLE_TYPE } from '../entities/model';
import { orgRoleFriendlyName, orgRoleSimpleName } from '../helpers';
import { OrgMemberRowAction, RoleLabel } from './common';

const columnHelper = createColumnHelper<OrganizationMember>();

export const OrgUsers = () => {
  const { activeOrg } = useOrgAppContext();
  const { isAllowed } = usePermissions();
  const canReadOrgUsers = isAllowed(ACTION.READ, `organizations/${activeOrg.id}/users`);
  const canReadOrgRoles = isAllowed(ACTION.READ, `organizations/${activeOrg.id}/roles`);
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState('');
  const isUpxConsistencyEnabled = getFlag('upxConsistency', false);
  // Remove null assertion when ActiveOrg becomes required
  const data = consoleApi.useListOrganizationMembersQuery(activeOrg.id, {
    skip: !canReadOrgUsers || !canReadOrgRoles,
  });

  const mfaTooltip = () => {
    return (
      <Tooltip type="rich">
        <Tooltip.Trigger>
          <InformationCircleIconOutline className="n-size-token-7 cursor-pointer" />
        </Tooltip.Trigger>
        <Tooltip.Content>
          <Tooltip.Header>Enrollment status</Tooltip.Header>
          <Tooltip.Body className="space-y-2">
            <div className="flex">
              <StatusIndicator type="success" as="span" className="mr-2 mt-1" />
              <span className="text-xs">Enrolled: User has enrolled MFA.</span>
            </div>
            <div className="flex">
              <StatusIndicator type="danger" as="span" className="mr-2 mt-1" />
              <span className="text-xs">Not enrolled: User has not completed enrollment.</span>
            </div>
            <div className="flex">
              <StatusIndicator type="info" as="span" className="mr-2 mt-1" />
              <span className="text-xs">
                Not required: User is not required to enroll MFA. Most likely due to using an external IdP.
              </span>
            </div>
          </Tooltip.Body>
        </Tooltip.Content>
      </Tooltip>
    );
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: 'User',
        ...(isUpxConsistencyEnabled && {
          cell: (cx) => {
            const row = cx.row.original;
            return (
              <div className="flex items-center gap-2">
                <Avatar shape="circle" size="medium" type="icon" />
                <span>{row.email}</span>
              </div>
            );
          },
        }),
      }),
      columnHelper.display({
        id: 'roles',
        header: () => {
          if (isUpxConsistencyEnabled) {
            return (
              <span className="flex items-center gap-1">
                <span>Organization role</span>
                <Tooltip type="simple">
                  <Tooltip.Trigger hasButtonWrapper>
                    <IconButton
                      isClean
                      size="small"
                      ariaLabel="Open organization role documentation"
                      as="a"
                      htmlAttributes={{
                        href: 'https://neo4j.com/docs/aura/user-management/#_organization_level_roles',
                        target: '_blank',
                        rel: 'noopener noreferrer',
                      }}
                    >
                      <QuestionMarkCircleIconOutline />
                    </IconButton>
                  </Tooltip.Trigger>
                  <Tooltip.Content>Open organization role documentation</Tooltip.Content>
                </Tooltip>
              </span>
            );
          }
          return 'Organization role';
        },
        cell: (cx) => (
          <>
            {cx.row.original.roles.map((role) => {
              if (isUpxConsistencyEnabled) {
                return cx.row.original.roles.length === 1 ? (
                  orgRoleSimpleName[role.name]
                ) : (
                  <RoleLabel key={role.id} roleType={ROLE_TYPE.ORG}>
                    {orgRoleSimpleName[role.name]}
                  </RoleLabel>
                );
              }
              return (
                <RoleLabel key={role.id} roleType={ROLE_TYPE.ORG}>
                  {orgRoleFriendlyName[role.name]}
                </RoleLabel>
              );
            })}
          </>
        ),
      }),
      ...(activeOrg.capabilities.app_mfa && activeOrg.appMfaRequired
        ? [
            columnHelper.display({
              id: 'mfa_enrollment',
              header: () => {
                return (
                  <span className="flex items-center gap-2">
                    <span>MFA enrollment</span>
                    {mfaTooltip()}
                  </span>
                );
              },
              cell: (cx) => {
                const row = cx.row.original;
                const status = row.appMfaStatus;
                if (isNullish(status)) {
                  return (
                    <>
                      <StatusIndicator type="danger" as="span" className="mr-2" />
                      <span>Not enrolled</span>
                    </>
                  );
                } else if (
                  status.reason === MFA_STATUS_REASON.EXTERNAL_AUTH_PROVIDER ||
                  status.reason === MFA_STATUS_REASON.SSO_APPLIED
                ) {
                  return (
                    <>
                      <StatusIndicator type="info" as="span" className="mr-2" />
                      <span>Not required</span>
                    </>
                  );
                } else if (status.enabled && status.reason !== MFA_STATUS_REASON.NOT_ENROLLED) {
                  return (
                    <>
                      <StatusIndicator type="success" as="span" className="mr-2" />
                      <span>Enrolled</span>
                    </>
                  );
                }

                return (
                  <>
                    <StatusIndicator type="danger" as="span" className="mr-2" />
                    <span>Not enrolled</span>
                  </>
                );
              },
            }),
          ]
        : []),
      columnHelper.display({
        id: 'actions',
        cell: (cx) => {
          const row = cx.row.original;
          return <OrgMemberRowAction orgUser={row} cx={cx} />;
        },
        enableResizing: false,
        ...(!isUpxConsistencyEnabled
          ? { minSize: 90, maxSize: 90 }
          : {
              meta: {
                // Object needed for RowActionCell to work.
                isActionCell: {
                  actions: [],
                },
              },
            }),
      }),
    ],
    [activeOrg.appMfaRequired, activeOrg.capabilities.app_mfa, isUpxConsistencyEnabled],
  );

  const table = useCommonTable({
    columns,
    data: data.data ?? [],
    initialState: {
      pagination: { pageSize: 25 },
    },
    defaultColumn: {
      minSize: 70,
    },
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    state: {
      globalFilter,
      ...(!isUpxConsistencyEnabled && { columnPinning: { right: ['actions'] } }),
    },
  });

  if (!canReadOrgUsers || !canReadOrgRoles) {
    void navigate(`/org/${activeOrg.id}/projects`, { replace: true });
    return null;
  }

  if (data.isError) {
    return (
      <Banner
        title="Something went wrong"
        description="Failed to fetch the organization users. Try again."
        type="danger"
        usage="inline"
      />
    );
  }

  if (isUpxConsistencyEnabled) {
    return (
      <div className="bg-palette-neutral-bg-weak border-palette-neutral-border-weak flex flex-col rounded-lg border">
        <DataGridHelpers.OuterHeader>
          <SearchField
            className="max-w-[396px] grow"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            htmlAttributes={{ 'aria-label': 'Search for organization user' }}
          />
        </DataGridHelpers.OuterHeader>
        <DataGrid
          tableInstance={table}
          isLoading={data.isLoading}
          isKeyboardNavigable={false}
          styling={{
            borderStyle: 'all-sides',
          }}
          rootProps={{
            className: 'rounded-b-lg overflow-hidden',
          }}
          components={{
            RowsPerPage: DataGridHelpers.RowsPerPageWithBodyMenuPortal,
          }}
        />
      </div>
    );
  }

  return (
    <DataGridHelpers.Wrapper>
      <DataGridHelpers.OuterHeader>
        <div className="flex w-full flex-wrap justify-between gap-2">
          <div className="flex basis-[400px] gap-2">
            <SearchField
              className="min-w-36 grow"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              htmlAttributes={{ 'aria-label': 'Search for organization user' }}
            />
          </div>
        </div>
      </DataGridHelpers.OuterHeader>
      <DataGridHelpers.DataGridRightColumnPinned<OrganizationMember>
        tableInstance={table}
        isLoading={data.isLoading}
        styling={{
          headerStyle: 'clean',
          borderStyle: 'all-sides',
        }}
        components={{
          RowsPerPage: DataGridHelpers.RowsPerPageWithBodyMenuPortal,
        }}
      />
    </DataGridHelpers.Wrapper>
  );
};
